import type { Job, V3 } from "@istari/istari-client";
import { JobStatusName } from "@istari/istari-client";

import { getLatestJobStatus, getLatestJobStatusName } from "~/jobs/model/job-status";

type StatusHistoryItem = NonNullable<Job["statusHistory"]>[number];

const TERMINAL_STATUSES: ReadonlySet<JobStatusName> = new Set([
  JobStatusName.Completed,
  JobStatusName.Failed,
  JobStatusName.Canceled,
]);

export function isTerminalStatus(status: JobStatusName): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function eventToStatusItem(event: V3.JobStatusChangedEvent): StatusHistoryItem {
  return {
    name: event.status,
    created: event.createdAt ?? new Date(),
    message: event.message ?? undefined,
  } as StatusHistoryItem;
}

/**
 * Append the event's status to a cached job, unless the job already has a newer transition.
 *
 * The live feed can deliver out of order on retry, so a status is applied only when its timestamp is
 * at or after the job's latest — a stale PENDING that arrives after COMPLETED never overwrites it.
 * Returns the same reference when nothing changes, so callers can skip a cache write.
 */
export function applyEventToJob(job: Job, event: V3.JobStatusChangedEvent): Job {
  const eventCreated = event.createdAt ?? new Date();
  const latest = getLatestJobStatus(job.statusHistory ?? undefined);
  if (latest?.created && latest.created.getTime() > eventCreated.getTime()) {
    return job;
  }
  const history = (job.statusHistory ?? []) as StatusHistoryItem[];
  // Job is a class; spreading drops its prototype methods, which cache consumers never call on a
  // patched row — preserve the shape with a cast rather than reconstructing the instance.
  return { ...job, statusHistory: [...history, eventToStatusItem(event)] } as Job;
}

export function jobLatestStatusName(job: Job): JobStatusName {
  return getLatestJobStatusName(job.statusHistory ?? undefined) ?? JobStatusName.Unknown;
}
